/**
 * @file context/ToastContext.jsx
 * @description Site-wide Bootstrap toast notification system.
 *
 * Provides an `addToast` function via React context so any component in the
 * tree can trigger a toast without prop-drilling. The ToastContainer is rendered
 * internally by this provider, fixed to the bottom-right corner of the viewport.
 *
 * Usage:
 *   const { addToast } = useToast();
 *   addToast('Token created successfully', 'success');
 *   addToast('Failed to load data', 'danger', 'Error');
 *
 * Variants map to Bootstrap alert colour classes:
 *   'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary'
 */

import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

// ── Configuration ─────────────────────────────────────────────────────────────

/**
 * How long each toast stays visible before auto-hiding (milliseconds).
 * Change this single constant to adjust timing across the whole app.
 */
export const TOAST_DURATION_MS = 3000;

// ── Context & hook ────────────────────────────────────────────────────────────

const ToastContext = createContext(null);

/**
 * Hook for consuming the toast context.
 * Must be used inside a <ToastProvider>.
 *
 * @returns {{ addToast: (message: string, variant?: string, title?: string) => void }}
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

// ── Provider ──────────────────────────────────────────────────────────────────

/**
 * @param {Object}          props
 * @param {React.ReactNode} props.children
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  // Refs for each toast's auto-hide timeout so we can cancel on manual close
  const timers = useRef({});

  /**
   * Adds a new toast to the stack and schedules its auto-removal.
   *
   * @param {string} message       - Body text shown in the toast.
   * @param {string} [variant='info'] - Bootstrap colour variant.
   * @param {string} [title='']    - Optional bold header text.
   */
  const addToast = useCallback((message, variant = 'info', title = '') => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { id, message, variant, title }]);

    // Schedule auto-removal after TOAST_DURATION_MS
    timers.current[id] = setTimeout(() => {
      removeToast(id);
    }, TOAST_DURATION_MS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Removes a toast by ID and cancels its auto-hide timer.
   * @param {string} id
   */
  const removeToast = useCallback((id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <ToastStack toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

// ── Toast stack renderer ──────────────────────────────────────────────────────

/**
 * Renders all active toasts stacked in the bottom-right corner.
 * Uses Bootstrap 5 toast markup with CSS-based show state (no BS5 JS needed).
 *
 * @param {Object}   props
 * @param {Array}    props.toasts         - Active toast objects.
 * @param {Function} props.onRemove       - Called with toast id when closed.
 */
function ToastStack({ toasts, onRemove }) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="toast-container position-fixed bottom-0 end-0 p-3"
      style={{ zIndex: 1100 }}
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

/**
 * Maps a variant name to Bootstrap toast header background and text colour.
 * @param {string} variant
 * @returns {{ bg: string, text: string, icon: string }}
 */
function variantStyle(variant) {
  const map = {
    success:   { bg: 'var(--color-accent-2)', text: '#000', icon: 'bi-check-circle-fill' },
    danger:    { bg: '#dc3545',               text: '#fff', icon: 'bi-x-circle-fill'     },
    warning:   { bg: 'var(--color-accent-1)', text: '#000', icon: 'bi-exclamation-triangle-fill' },
    info:      { bg: 'var(--color-accent-3)', text: '#000', icon: 'bi-info-circle-fill'  },
    primary:   { bg: 'var(--color-primary)',  text: '#fff', icon: 'bi-bell-fill'         },
    secondary: { bg: '#6c757d',               text: '#fff', icon: 'bi-bell-fill'         },
  };
  return map[variant] ?? map.info;
}

/**
 * Single Bootstrap 5 toast element.
 *
 * @param {Object}   props
 * @param {Object}   props.toast     - { id, message, variant, title }
 * @param {Function} props.onRemove  - Called with toast id on close.
 */
function ToastItem({ toast, onRemove }) {
  const { bg, text, icon } = variantStyle(toast.variant);

  return (
    <div
      className="toast show mb-2"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      style={{ minWidth: 280 }}
    >
      <div
        className="toast-header"
        style={{ backgroundColor: bg, color: text }}
      >
        <i className={`bi ${icon} me-2`} aria-hidden="true" />
        <strong className="me-auto">{toast.title || capitalise(toast.variant)}</strong>
        <button
          type="button"
          className="btn-close"
          style={{ filter: text === '#fff' ? 'invert(1)' : 'none' }}
          onClick={() => onRemove(toast.id)}
          aria-label="Close toast"
        />
      </div>
      <div className="toast-body bg-white">
        {toast.message}
      </div>
    </div>
  );
}

/** Capitalises the first character of a string. */
function capitalise(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
}
